// frontend/src/hooks/useCRSFormState.js
import { useState, useEffect, useCallback } from 'react';
import { BAD_VALUE } from '../constants/crsFormOptions'; // Adjust path as needed

const initialFormData = {
  // Personal Info
  name: '',
  maritalStatus: BAD_VALUE,
  spouseOrPartnerIsPR: BAD_VALUE,
  spouseOrPartnerWillAccompany: BAD_VALUE,
  age: BAD_VALUE,

  // Education
  educationLevel: BAD_VALUE,
  studiedInCanada: BAD_VALUE,
  canadianEducationLevel: BAD_VALUE,

  // Language
  firstLanguageTestValid: BAD_VALUE,
  firstLanguageTest: BAD_VALUE,
  firstLanguageScores: { speaking: BAD_VALUE, listening: BAD_VALUE, reading: BAD_VALUE, writing: BAD_VALUE },
  hasSecondLanguageTest: BAD_VALUE,
  secondLanguageTest: BAD_VALUE,
  secondLanguageScores: { speaking: BAD_VALUE, listening: BAD_VALUE, reading: BAD_VALUE, writing: BAD_VALUE },

  // Work Experience
  canadianWorkExperience: BAD_VALUE,
  foreignWorkExperience: BAD_VALUE,

  // Additional Factors
  hasCertificateOfQualification: BAD_VALUE,
  hasValidJobOffer: BAD_VALUE,
  jobOfferNocTeer: BAD_VALUE,
  hasProvincialNomination: BAD_VALUE,
  hasSiblingInCanada: BAD_VALUE,

  // Spouse Factors (Only if applicable)
  spouseEducationLevel: BAD_VALUE,
  spouseCanadianWorkExperience: BAD_VALUE,
  spouseLanguageTest: BAD_VALUE,
  spouseLanguageScores: { speaking: BAD_VALUE, listening: BAD_VALUE, reading: BAD_VALUE, writing: BAD_VALUE },
};

function useCRSFormState(setCurrentField) {
  const [formData, setFormData] = useState(initialFormData);
  const [lastFocusedField, setLastFocusedField] = useState(null);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('crsFormDataOfficial');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Merge saved data with initial state, ensuring nested objects are handled
        setFormData(prevData => ({
          ...prevData,
          ...parsed,
          firstLanguageScores: parsed.firstLanguageScores || { ...initialFormData.firstLanguageScores },
          secondLanguageScores: parsed.secondLanguageScores || { ...initialFormData.secondLanguageScores },
          spouseLanguageScores: parsed.spouseLanguageScores || { ...initialFormData.spouseLanguageScores },
        }));
      } catch (error) {
        console.error("Failed to parse crsFormDataOfficial from localStorage", error);
        localStorage.removeItem('crsFormDataOfficial');
      }
    }
  }, []); // Runs only once on mount

  // Save data to localStorage whenever formData changes
  useEffect(() => {
    // Avoid saving initial empty state immediately
    // Check a field that's usually filled early, like age
    if (formData.age !== BAD_VALUE) {
       localStorage.setItem('crsFormDataOfficial', JSON.stringify(formData));
    }
  }, [formData]);

  // Effect to update the external current field tracker
   useEffect(() => {
     if (setCurrentField && lastFocusedField) {
       setCurrentField(lastFocusedField);
     }
   }, [lastFocusedField, setCurrentField]);

  // Unified handler for simple inputs (Select, Switch)
  const handleChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    let newValue = type === 'checkbox' || type === 'switch' ? (checked ? 'B' : 'A') : value;

    // Special handling for boolean-like selects mapped to 'A'/'B'
    const booleanSelects = [
        'spouseOrPartnerIsPR', 'spouseOrPartnerWillAccompany', 'studiedInCanada',
        'hasCertificateOfQualification', 'hasValidJobOffer', 'hasProvincialNomination',
        'hasSiblingInCanada', 'firstLanguageTestValid', 'hasSecondLanguageTest'
    ];
    if (booleanSelects.includes(name) && typeof checked !== 'undefined') {
        newValue = checked ? 'B' : 'A';
    }

    setFormData(prevData => {
      const newData = { ...prevData, [name]: newValue };

      // --- Reset dependent fields logic ---
      if (name === 'maritalStatus') {
          const isNowMarried = newValue === 'B' || newValue === 'E';
          if (!isNowMarried) {
              newData.spouseOrPartnerIsPR = BAD_VALUE;
              newData.spouseOrPartnerWillAccompany = BAD_VALUE;
              newData.spouseEducationLevel = BAD_VALUE;
              newData.spouseCanadianWorkExperience = BAD_VALUE;
              newData.spouseLanguageTest = BAD_VALUE;
              newData.spouseLanguageScores = { ...initialFormData.spouseLanguageScores };
          }
      }
      if (name === 'spouseOrPartnerIsPR' && newValue === 'B') {
          newData.spouseOrPartnerWillAccompany = BAD_VALUE;
          newData.spouseEducationLevel = BAD_VALUE;
          newData.spouseCanadianWorkExperience = BAD_VALUE;
          newData.spouseLanguageTest = BAD_VALUE;
          newData.spouseLanguageScores = { ...initialFormData.spouseLanguageScores };
      }
      if (name === 'spouseOrPartnerWillAccompany' && newValue === 'A') {
          newData.spouseEducationLevel = BAD_VALUE;
          newData.spouseCanadianWorkExperience = BAD_VALUE;
          newData.spouseLanguageTest = BAD_VALUE;
          newData.spouseLanguageScores = { ...initialFormData.spouseLanguageScores };
      }
      if (name === 'studiedInCanada' && newValue === 'A') {
          newData.canadianEducationLevel = BAD_VALUE;
      }
      if (name === 'firstLanguageTestValid' && newValue === 'A') {
          newData.firstLanguageTest = BAD_VALUE;
          newData.firstLanguageScores = { ...initialFormData.firstLanguageScores };
          newData.hasSecondLanguageTest = BAD_VALUE;
          newData.secondLanguageTest = BAD_VALUE;
          newData.secondLanguageScores = { ...initialFormData.secondLanguageScores };
      }
      if (name === 'firstLanguageTest') {
          newData.firstLanguageScores = { ...initialFormData.firstLanguageScores };
          newData.hasSecondLanguageTest = BAD_VALUE;
          newData.secondLanguageTest = BAD_VALUE;
          newData.secondLanguageScores = { ...initialFormData.secondLanguageScores };
      }
      if (name === 'secondLanguageTest') {
          newData.secondLanguageScores = { ...initialFormData.secondLanguageScores };
          if (newValue === 'C') {
              newData.hasSecondLanguageTest = 'A'; // Mark as 'No' effectively
          } else if (newValue !== BAD_VALUE) {
              newData.hasSecondLanguageTest = 'B'; // Mark as 'Yes'
          } else {
              newData.hasSecondLanguageTest = BAD_VALUE;
          }
      }
      if (name === 'hasValidJobOffer' && newValue === 'A') {
          newData.jobOfferNocTeer = BAD_VALUE;
      }
      if (name === 'spouseLanguageTest') {
          newData.spouseLanguageScores = { ...initialFormData.spouseLanguageScores };
      }

      setLastFocusedField(name);
      return newData;
    });
  }, []); // Empty dependency array as it doesn't depend on external state/props

  // Specific handler for nested language score updates
  const handleLanguageScoreChange = useCallback((langType, skillName, value) => {
    setFormData(prevData => {
      const newState = {
        ...prevData,
        [langType]: {
          ...prevData[langType],
          [skillName]: value,
        }
      };
      setLastFocusedField(`${langType}.${skillName}`);
      return newState;
    });
  }, []); // Empty dependency array

  const handleFocus = useCallback((fieldName) => {
    setLastFocusedField(fieldName);
  }, []); // No dependencies needed

  return {
    formData,
    handleChange,
    handleLanguageScoreChange,
    handleFocus,
    // setFormData, // Expose if direct setting is needed outside the hook
    // lastFocusedField // Expose if needed externally
  };
}

export default useCRSFormState;